// cSpell:ignore pand
// Maps between the dashboard `ApportionmentKey` shape and the rentio-api-next
// `ApportionmentKey` DTOs. Frontend `groupId` (string) ↔ backend `propertyId`
// (number). Stored `weights` are cleaned on the way in (see sanitizeWeights).

import {
  IApportionmentKey,
  ICreateApportionmentKey,
  IUpdateApportionmentKey,
} from "@rentiohq/shared-frontend/dist/reduxV2/apportionmentKey";
import { ApportionmentKey } from "./types";

// Only positive, finite numbers count as a weight. A weight stored as a
// string was concatenated instead of added ("1" + 2 = "12") and a negative or
// non-numeric one turned every split into NaN; such a weight now simply
// counts as none, so the "pand not in the key" warnings cover it.
const sanitizeWeights = (
  weights: Record<string, unknown> | null | undefined,
): Record<string, number> => {
  const out: Record<string, number> = {};
  for (const [unitId, raw] of Object.entries(weights ?? {})) {
    const weight = typeof raw === "string" ? Number(raw) : raw;
    if (typeof weight === "number" && Number.isFinite(weight) && weight > 0) {
      out[unitId] = weight;
    }
  }
  return out;
};

export const keyFromDto = (dto: IApportionmentKey): ApportionmentKey => ({
  id: dto.id,
  groupId: String(dto.propertyId),
  name: dto.name,
  isSystem: dto.isSystem,
  isEqual: dto.isEqual,
  weights: sanitizeWeights(dto.weights),
  createdAt: dto.createdAt ?? "",
  updatedAt: dto.modifiedAt ?? dto.createdAt ?? "",
});

export const keyToCreateDto = (
  key: Omit<ApportionmentKey, "id" | "createdAt" | "updatedAt" | "isSystem">,
): ICreateApportionmentKey => ({
  propertyId: Number(key.groupId),
  name: key.name,
  isEqual: key.isEqual,
  weights: key.weights,
});

export const keyPatchToUpdateDto = (
  patch: Partial<ApportionmentKey>,
): IUpdateApportionmentKey => {
  const dto: IUpdateApportionmentKey = {};
  if ("name" in patch) dto.name = patch.name;
  if ("isEqual" in patch) dto.isEqual = patch.isEqual;
  if ("weights" in patch) dto.weights = patch.weights;
  return dto;
};
